import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { DateAdapter, MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatCalendar, MatCalendarHeader } from '@angular/material/datepicker';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NgFor, NgForOf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-p-ticket-filter-preset',
  standalone: true,
  providers: [
    provideNativeDateAdapter()
  ],
  imports: [
    NgFor,
    NgForOf,
    MatButtonModule,
    MatIconModule,
    MatNativeDateModule
  ],
  templateUrl: './p-ticket-filter-preset.component.html',
  styleUrl: './p-ticket-filter-preset.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PTicketFilterPresetComponent<D> {
  // list of range presets we want to provide:
  @Input() customPresets: string[] = [

    'Last 30 days',

    'Last 6 months',

    'Last year',

    'All'
  ];
  @Input() selectedDate: D | null = null; // Current selected date
  @Output() selectedDateChange = new EventEmitter<D>(); // Emit new date
  static presetSelected = new EventEmitter<{ start: any, end: any }>();
  selectedRange: string = '';
  @HostBinding('class.touch-ui')
  readonly isTouchUi = this.picker.touchUi;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private dateAdapter: DateAdapter<D>,
    private picker: MatDateRangePicker<D>,
    private calendar: MatCalendar<D>,
    cdr: ChangeDetectorRef
  ) {
    calendar.stateChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      cdr.markForCheck();
      this.loadSelectedRange();
    });
  }

  ngOnInit() {
    this.loadSelectedRange();
  }

  loadSelectedRange() {
    this.selectedRange = '';
    for (const preset of this.customPresets) {
      if (this.isSelectdeRange(preset)) {
        this.selectedRange = preset;
        break;
      }
    }
  }

  // called when user selects a range preset:
  selectRange(rangeName: string): void {
    if (rangeName === 'Custom') return;
    const [start, end] = this.calculateDateRange(rangeName);
    (this.calendar.selected as any).start = start;
    (this.calendar.selected as any).end = end;
    this.calendar.updateTodaysDate();
    this.calendar.activeDate = start;
    this.calendar.activeDate = end;
    PTicketFilterPresetComponent.presetSelected.emit({ start, end });
  }

  private calculateDateRange(rangeName: string): [start: D, end: D] {
    const today = this.today;
    const year = this.dateAdapter.getYear(today);

    switch (rangeName) {

      case 'Last 30 days': {
        const start = this.dateAdapter.addCalendarDays(today, -30);
        return [start, today];
      }
      case 'Last 6 months': {
        const start = this.dateAdapter.addCalendarDays(today, -30 * 6);
        return [start, today];
      }
      case 'Last year': {
        const start = this.dateAdapter.addCalendarDays(today, -365);
        return [start, today];
      }
      case 'All': {
        const start = this.dateAdapter.createDate(2023,0,1); 
        return [start, today];
      }
      case 'Custom': {
        // Ensure that selectedRange is properly populated
        const selectedRange: any = this.calendar.selected; // Be sure `this.calendar.selected` is valid
        if (selectedRange && selectedRange.start && selectedRange.end) {
          return [selectedRange.start, selectedRange.end];
        } else {
          // Handle invalid or empty custom range selection (optional fallback)
          console.warn("Custom range is invalid.");
          return [today, today];
        }
      }

      default: {
        // Provide fallback or throw an error if the rangeName doesn't match any case
        throw new Error(`Unknown range name: ${rangeName}`);
      }
    }
  }



  private get today(): D {
    const today = this.dateAdapter.today(); //.getValidDateOrNull(new Date());
    if (today === null) {
      throw new Error('date creation failed');
    }
    return today;
  }
  get selectedDateRange() {
    const selectedRange: any = this.calendar.selected;
    let displayText = '';
    if (selectedRange.start !== null)
      displayText += new Date(String(selectedRange.start)).toLocaleDateString();
    if (selectedRange.end !== null)
      displayText +=
        ' - ' + new Date(String(selectedRange.end)).toLocaleDateString();
    return displayText;
  }

  ngOnDestroy() {
    this.destroy$.next(); // will trigger unsubscription in takeUntil
  }

  isSelectdeRange(rangeName: string) {
    const [start, end] = this.calculateDateRange(rangeName);
    const selectedRange: any = this.calendar.selected;
    return (
      selectedRange.start !== null &&
      selectedRange.end !== null &&
      new Date(String(selectedRange.start)).toDateString() ===
      new Date(String(start)).toDateString() &&
      new Date(String(selectedRange.end)).toDateString() ===
      new Date(String(end)).toDateString()
    );
  }


  previousMonth(): void {
    this.calendar.activeDate = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1);
  }

  nextMonth(): void {
    this.calendar.activeDate = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1);
  }

  getMonthYearLabel(): string {
    return this.dateAdapter.format(this.calendar.activeDate, 'MMMM yyyy');
  }
}
